import React, { useState, useEffect } from "react";
import { getScholarProfile } from "../services/adminService";
import { formatScholarId } from "../utils/idFormatter";

interface ScholarApprovalModalProps {
  scholarId: string | null;
  scholarName: string;
  open: boolean;
  onClose: () => void;
  onApproveConfirm: (id: string, name: string) => void;
  processing?: boolean;
}

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, zIndex: 10000,
  background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)",
  display: "flex", alignItems: "center", justifyContent: "center",
};

const modal: React.CSSProperties = {
  background: "hsl(var(--card))", borderRadius: 20, width: "min(96vw, 780px)",
  maxHeight: "92vh", display: "flex", flexDirection: "column",
  boxShadow: "0 25px 60px rgba(0,0,0,0.22)",
  animation: "profileSlideUp .25s ease",
  border: "1px solid hsl(var(--border))",
};

const row: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", padding: "8px 0",
  borderBottom: "1px solid hsl(var(--border)/0.5)", gap: 12,
};

const rowLabel: React.CSSProperties = { color: "#64748b", fontSize: 13, flexShrink: 0 };
const rowValue: React.CSSProperties = { fontWeight: 600, fontSize: 13, textAlign: "right", wordBreak: "break-all" };

// ── Image viewer ──────────────────────────────────────────────────────
const DocumentImage: React.FC<{ url: string | null | undefined; label: string }> = ({ url, label }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);

  if (!url) {
    return (
      <div style={{ padding: "12px 16px", background: "#fef3c7", borderRadius: 8, fontSize: 12, color: "#92400e", fontStyle: "italic" }}>
         {label} not uploaded
      </div>
    );
  }

  // If the URL is a storage path (no http), it's a raw bucket path — just show as link

  return (
    <>
      <div
        onClick={() => !error && setOpen(true)}
        style={{
          border: "2px dashed hsl(var(--border))", borderRadius: 10, overflow: "hidden",
          cursor: error ? "default" : "pointer", position: "relative", background: "#f8fafc",
          minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {!error ? (
          <>
            <img
              src={url}
              alt={label}
              onError={() => setError(true)}
              style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain", display: "block" }}
            />
            <div style={{
              position: "absolute", bottom: 6, right: 6,
              background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 11,
              padding: "3px 8px", borderRadius: 6, fontWeight: 600,
            }}>
              Click to enlarge
            </div>
          </>
        ) : (
          <div style={{ padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}></div>
            <a href={url} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 600, fontSize: 12 }}>
              View {label} ↗
            </a>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 20000,
            background: "rgba(0,0,0,0.85)", display: "flex",
            alignItems: "center", justifyContent: "center", padding: 24,
          }}
        >
          <img
            src={url}
            alt={label}
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 12 }}
          />
          <button
            onClick={() => setOpen(false)}
            style={{
              position: "absolute", top: 20, right: 24,
              width: 40, height: 40, borderRadius: "50%",
              background: "rgba(255,255,255,0.15)", border: "none",
              color: "#fff", fontSize: 20, cursor: "pointer",
            }}
          >✕</button>
        </div>
      )}
    </>
  );
};

// ── Section wrapper ───────────────────────────────────────────────────
const Section: React.FC<{
  title: string;
  checkKey: keyof { identity: boolean; religion: boolean; payout: boolean; services: boolean };
  checked: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, checkKey, checked, onToggle, children }) => (
  <div style={{
    background: "hsl(var(--card))", borderRadius: 12, padding: 20,
    border: "1px solid hsl(var(--border))", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))" }}>{title}</h3>
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, fontSize: 13, color: checked ? "#10b981" : "#94a3b8" }}>
        <input type="checkbox" checked={checked} onChange={onToggle} style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#10b981" }} />
        {checked ? "✓ Verified" : "Mark Verified"}
      </label>
    </div>
    {children}
  </div>
);

const InfoRow: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <div style={row}>
    <span style={rowLabel}>{label}</span>
    <span style={rowValue}>{value || "—"}</span>
  </div>
);

const ScholarApprovalModal: React.FC<ScholarApprovalModalProps> = ({ scholarId, scholarName, open, onClose, onApproveConfirm, processing = false }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  const [checks, setChecks] = useState({
    identity: false,
    religion: false,
    payout: false,
    services: false,
  });

  useEffect(() => {
    if (open && scholarId) {
      setChecks({ identity: false, religion: false, payout: false, services: false });
      setData(null);
      setLoading(true);
      getScholarProfile(scholarId).then(res => {
        setData(res);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }
  }, [open, scholarId]);

  if (!open) return null;

  const allChecked = Object.values(checks).every(Boolean);
  const handleCheckbox = (key: keyof typeof checks) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));

  // Safely extract nested JSON fields
  const identityData  = data?.identityVerification  as Record<string, any> | null;
  const religiousData = data?.religiousCertification as Record<string, any> | null;
  const paymentData   = data?.paymentDetails         as Record<string, any> | null;
  const services      = data?.services || [];
  const user          = data?.user;

  return (
    <>
      <style>{`
        @keyframes profileSlideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={modal} onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div style={{ padding: "20px 28px 16px", borderBottom: "1px solid hsl(var(--border))", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {data?.profileImageUrl ? (
                <img
                  src={data.profileImageUrl}
                  alt={scholarName}
                  style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", border: "2px solid hsl(var(--border))" }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                <div style={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 800, color: "#fff", fontSize: 20,
                }}>
                  {(scholarName || "U").charAt(0)}
                </div>
              )}
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "hsl(var(--foreground))" }}>Review & Approve</h2>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{scholarName} ({formatScholarId(scholarId || data?.id)}) · {data?.religion || ""}</div>
              </div>
            </div>
            <button onClick={onClose} style={{
              width: 36, height: 36, borderRadius: "50%", border: "1px solid hsl(var(--border))",
              background: "hsl(var(--card))", cursor: "pointer", fontSize: 18, color: "#94a3b8",
            }}>✕</button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 28px", display: "flex", flexDirection: "column", gap: 16, background: "hsl(var(--muted)/0.3)" }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                Loading verification data…
              </div>
            ) : !data ? (
              <div style={{ textAlign: "center", padding: 60, color: "#ef4444" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}></div>
                Failed to load data. Please close and try again.
              </div>
            ) : (
              <>
                {/* ── Personal Info ── */}
                <div style={{ background: "hsl(var(--card))", borderRadius: 12, padding: 20, border: "1px solid hsl(var(--border))" }}>
                  <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))" }}>Personal Information</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
                    <InfoRow label="Full Name" value={user?.name} />
                    <InfoRow label="Email" value={user?.email} />
                    <InfoRow label="Phone" value={user?.phone} />
                    <InfoRow label="Religion" value={data?.religion} />
                    <InfoRow label="Gender" value={user?.gender} />
                    <InfoRow label="Date of Birth" value={user?.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : null} />
                    <InfoRow label="Scholar Type" value={data?.scholarType} />
                    <InfoRow label="Specialization" value={data?.specialization} />
                    <InfoRow label="Years of Experience" value={data?.yearsOfExperience} />
                    <InfoRow label="Languages" value={Array.isArray(data?.languages) ? data.languages.join(", ") : data?.languages} />
                    <InfoRow label="Community" value={data?.community} />
                    <InfoRow label="City / State" value={[data?.city, data?.state].filter(Boolean).join(", ")} />
                  </div>
                  {data?.bio && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid hsl(var(--border)/0.5)" }}>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 4 }}>BIO</div>
                      <div style={{ fontSize: 13, color: "hsl(var(--foreground))", lineHeight: 1.6 }}>{data.bio}</div>
                    </div>
                  )}
                </div>

                {/* ── Identity Verification ── */}
                <Section title="Identity Verification" checkKey="identity" checked={checks.identity} onToggle={() => handleCheckbox("identity")}>
                  {identityData ? (
                    <>
                      <InfoRow label="ID Type"          value={identityData.idType} />
                      <InfoRow label="ID Number"         value={identityData.idNumber} />
                      <InfoRow label="Verification Status" value={identityData.verificationStatus || (identityData.isVerified ? "Verified" : "Pending")} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                        <div>
                          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>IDENTITY DOCUMENT</div>
                          {/* documentImage is set during onboarding in scholars.service.ts */}
                          <DocumentImage url={identityData.documentImage || identityData.documentUrl} label="Identity Document" />
                        </div>
                        {(identityData.selfieImage) && (
                          <div>
                            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>SELFIE / PHOTO</div>
                            <DocumentImage url={identityData.selfieImage} label="Selfie" />
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13 }}>No identity data provided.</div>
                  )}
                </Section>

                {/* ── Religious Certification ── */}
                <Section title="Religious Certification" checkKey="religion" checked={checks.religion} onToggle={() => handleCheckbox("religion")}>
                  {religiousData ? (
                    <>
                      <InfoRow label="Institution Name" value={religiousData.institutionName || religiousData.institution} />
                      <InfoRow label="Reference Type"   value={religiousData.referenceType || religiousData.degree} />
                      <InfoRow label="Reference Person" value={religiousData.referencePerson} />
                      <InfoRow label="Reference Phone"  value={religiousData.referencePhone} />
                      <InfoRow label="Reference Email"  value={religiousData.referenceEmail} />
                      {/* Certificates are an array [{id, name, file, issueDate, expiryDate}] */}
                      {Array.isArray(religiousData.certificates) && religiousData.certificates.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>CERTIFICATES ({religiousData.certificates.length})</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            {religiousData.certificates.map((cert: any, idx: number) => (
                              <div key={cert.id || idx}>
                                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{cert.name || `Certificate ${idx + 1}`}</div>
                                <DocumentImage url={cert.file} label={cert.name || `Certificate ${idx + 1}`} />
                                {cert.issueDate && (
                                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>Issued: {cert.issueDate}{cert.expiryDate ? ` · Expires: ${cert.expiryDate}` : ""}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {religiousData.referenceLetter && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>REFERENCE LETTER</div>
                          <DocumentImage url={religiousData.referenceLetter} label="Reference Letter" />
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13 }}>No religious certification provided.</div>
                  )}
                </Section>

                {/* ── Payout Details ── */}
                <Section title="Payout Setup" checkKey="payout" checked={checks.payout} onToggle={() => handleCheckbox("payout")}>
                  {paymentData ? (
                    <>
                      <InfoRow label="Account Holder"      value={paymentData.accountHolder || paymentData.accountHolderName} />
                      <InfoRow label="Bank Name"            value={paymentData.bankName} />
                      <InfoRow label="Branch"               value={paymentData.branch} />
                      <InfoRow label="Account Type"         value={paymentData.accountType} />
                      <InfoRow label="Account Number"       value={paymentData.accountNumber ? `****${String(paymentData.accountNumber).slice(-4)}` : null} />
                      <InfoRow label="IFSC Code"             value={paymentData.ifscCode || paymentData.routingCode} />
                      <InfoRow label="UPI ID"                value={paymentData.upiId} />
                      {paymentData.termsAccepted !== undefined && (
                        <InfoRow label="Terms Accepted" value={paymentData.termsAccepted ? "✓ Yes" : "✗ No"} />
                      )}
                    </>
                  ) : (
                    <div style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13 }}>No payment details provided.</div>
                  )}
                </Section>

                {/* ── Services ── */}
                <Section title="Services Configured" checkKey="services" checked={checks.services} onToggle={() => handleCheckbox("services")}>
                  {services?.length > 0 ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {services.map((s: any) => (
                        <div key={s.id} style={{
                          padding: "6px 14px", background: "#f0fdf4", color: "#166534",
                          borderRadius: 8, fontSize: 13, fontWeight: 600,
                          border: "1px solid #bbf7d0",
                          display: "flex", alignItems: "center", gap: 6,
                        }}>
                          <span>{s.service?.name || "Unknown"}</span>
                          <span style={{ opacity: 0.6, fontWeight: 400 }}>₹{s.price}</span>
                          {s.durationMin && <span style={{ opacity: 0.5, fontSize: 11 }}>{s.durationMin}m</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 13 }}>No services configured.</div>
                  )}
                </Section>

                {/* ── Checklist summary ── */}
                <div style={{
                  background: allChecked ? "#f0fdf4" : "#fff7ed",
                  border: `1px solid ${allChecked ? "#bbf7d0" : "#fed7aa"}`,
                  borderRadius: 10, padding: "12px 16px",
                  display: "flex", alignItems: "center", gap: 10, fontSize: 13,
                  color: allChecked ? "#166534" : "#92400e", fontWeight: 600,
                }}>
                  <span style={{ fontSize: 18 }}>{allChecked ? "" : "⏳"}</span>
                  {allChecked
                    ? "All sections verified — ready to approve."
                    : `${Object.values(checks).filter(Boolean).length} of 4 sections verified. Check all sections before approving.`}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: "16px 28px", borderTop: "1px solid hsl(var(--border))", display: "flex", justifyContent: "flex-end", gap: 12, background: "hsl(var(--card))" }}>
            <button onClick={onClose} style={{
              padding: "10px 20px", borderRadius: 10, background: "hsl(var(--muted))",
              color: "hsl(var(--muted-foreground))", fontWeight: 700, border: "none", cursor: "pointer",
            }}>
              Cancel
            </button>
            <button
              onClick={() => scholarId && onApproveConfirm(scholarId, scholarName)}
              disabled={!allChecked || loading || processing}
              style={{
                padding: "10px 28px", borderRadius: 10, border: "none",
                background: (allChecked && !processing) ? "linear-gradient(135deg,#10b981,#059669)" : "hsl(var(--muted))",
                color: (allChecked && !processing) ? "#fff" : "hsl(var(--muted-foreground))",
                fontWeight: 700, cursor: (allChecked && !processing) ? "pointer" : "not-allowed",
                transition: "all .15s",
                boxShadow: (allChecked && !processing) ? "0 4px 14px rgba(16,185,129,0.35)" : "none",
                opacity: processing ? 0.6 : 1,
              }}
            >
              {processing ? "..." : "✓ Approve Scholar"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScholarApprovalModal;
